import type { ScheduleCar } from './types.js';

/**
 * 319 (#634) — the schedule's rows gathered by location or category. A presentation of the cars
 * already loaded: no request changes (FR-011), and the fleet's own order (301's global
 * `sort_order`, which the server sends) is kept inside every group (FR-004).
 *
 * 396 (#1342) — or by host: a car joins the group of EVERY live host it has (377: no main host), so a car with two
 * hosts is drawn twice, once per group. Each copy's row and bar ids carry the group's key (`copyId`), because the
 * timeline identifies rows and bars by id; `carIdOfRow` takes a drop target back to its car.
 */

export type GroupMode = 'none' | 'location' | 'category' | 'host';

export interface CarGroup {
  /** The location or category id, or `none` for the catch-all group. */
  key: string;
  name: string;
  cars: ScheduleCar[];
}

const CATCH_ALL: Record<Exclude<GroupMode, 'none'>, string> = {
  location: 'No location',
  category: 'No category',
  host: 'No host',
};

/** The separator between an id and its copy key; ObjectIds never contain it. */
const COPY_SEP = '@';

/** 396: a row's or bar's id in the copy drawn under `copyKey` (unchanged outside host grouping). */
export function copyId(id: string, copyKey: string | undefined): string {
  return copyKey ? `${id}${COPY_SEP}${copyKey}` : id;
}

/** 396: the car a (possibly copied) row id stands for. */
export function carIdOfRow(rowId: string): string {
  const at = rowId.indexOf(COPY_SEP);
  return at === -1 ? rowId : rowId.slice(0, at);
}

/** Which groups a car belongs to: named locations/categories/hosts, or the catch-all (FR-003; 396 FR-002). */
function groupsOf(
  car: ScheduleCar,
  mode: Exclude<GroupMode, 'none'>,
): Array<{ key: string; name: string }> {
  if (mode === 'host') {
    const seen = new Set<string>();
    const hosts = (car.hosts ?? []).filter((h) => !seen.has(h.id) && seen.add(h.id));
    return hosts.length > 0
      ? hosts.map((h) => ({ key: h.id, name: h.name }))
      : [{ key: 'none', name: CATCH_ALL.host }];
  }
  const field = mode === 'location' ? car.location : car.category;
  if (field?.name) return [{ key: field.id, name: field.name }];
  return [{ key: 'none', name: CATCH_ALL[mode] }];
}

/** Null for `none` (the single list); otherwise groups by name (then key: same names stay apart), the catch-all last. */
export function groupCars(cars: readonly ScheduleCar[], mode: GroupMode): CarGroup[] | null {
  if (mode === 'none') return null;
  const groups = new Map<string, CarGroup>();
  for (const car of cars) {
    for (const { key, name } of groupsOf(car, mode)) {
      const group = groups.get(key);
      if (group) group.cars.push(car);
      else groups.set(key, { key, name, cars: [car] });
    }
  }
  return [...groups.values()].sort((a, b) => {
    if (a.key === 'none') return 1;
    if (b.key === 'none') return -1;
    return a.name.localeCompare(b.name) || a.key.localeCompare(b.key);
  });
}

/**
 * US2 (FR-006a) — which groups are collapsed, per mode, kept in this browser (like 318's density):
 * a per-viewer focus, never in the address. Every access is guarded; unreadable means nothing
 * collapsed.
 */
export const COLLAPSED_STORAGE_KEY = 'carii-admin-schedule-collapsed';

export interface CollapsedState {
  location: string[];
  category: string[];
  /** 396: absent in a value stored before host grouping (read as none collapsed). */
  host: string[];
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const isKeyList = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((k) => typeof k === 'string');

export function readCollapsed(storage: StorageLike | undefined): CollapsedState {
  try {
    const raw = storage?.getItem(COLLAPSED_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
    if (parsed && isKeyList(parsed.location) && isKeyList(parsed.category)) {
      // 396: a value stored before host grouping has no host list; it keeps its other lists.
      const host = isKeyList(parsed.host) ? parsed.host : [];
      return { location: parsed.location, category: parsed.category, host };
    }
  } catch {
    // Unreadable (private mode, blocked data, corrupt value): treat as nothing collapsed.
  }
  return { location: [], category: [], host: [] };
}

export function writeCollapsed(storage: StorageLike | undefined, state: CollapsedState): void {
  try {
    storage?.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Still applies for this visit; simply not remembered.
  }
}

export function toggleCollapsed(
  state: CollapsedState,
  mode: Exclude<GroupMode, 'none'>,
  key: string,
): CollapsedState {
  const keys = state[mode];
  return {
    ...state,
    [mode]: keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key],
  };
}
